/* =====================================================
   Cart subscription logic (UI memory + safe hydration)
   ===================================================== */

/* ========== keep your working server update ========== */

// Force-refresh the drawer HTML from the cart section, then re-hydrate the select
async function refreshCartDrawer(){
  try {
    const res  = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
    const html = await res.text();
    const doc  = new DOMParser().parseFromString(html, 'text/html');

    ['cart-drawer-items', '.cart-drawer__footer'].forEach((sel) => {
      const target = document.querySelector(sel);
      const source = doc.querySelector(sel);
      if (target && source) target.replaceWith(source);
    });

    // make sure your remembered selection shows after the DOM swap
    setTimeout(() => hydrateSelectionFromMemory(document), 0);
  } catch (e) {
    console.error('refreshCartDrawer error →', e);
  }
}

// ---------- helpers ----------
function getLineQty(container){
  const line = Number(container.dataset.line);
  const el = document.getElementById(`Drawer-quantity-${line}`) || document.getElementById(`Quantity-${line}`);
  const q = el ? parseInt(el.value, 10) : NaN;
  return Number.isFinite(q) && q > 0 ? q : 1;
}

function validPlanForContainer(container, planId){
  const allowed = (container.dataset.allowedPlans || '').split(',').filter(Boolean);
  if (!planId) return '';
  const cand = String(planId);
  return allowed.includes(cand) ? cand : '';
}

function fetchCartJson(){
  return fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
}

// ---------- change existing line's selling_plan (native) ----------
async function changeLineSellingPlan(container, planIdOrEmpty){
  const key = container.dataset.key;
  const quantity = getLineQty(container);
  const selling_plan = planIdOrEmpty || '';

  const payload = { id: key, quantity, selling_plan };
  console.log('[CHANGE] payload →', payload);

  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: {'Content-Type':'application/json'},
    body: JSON.stringify(payload)
  });

  const text = await res.text();
  let cart;
  try { cart = JSON.parse(text); }
  catch (e) {
    console.warn('[CHANGE] non-JSON response', { status: res.status, text });
    throw new Error('Cart change returned non-JSON');
  }
  console.log('[CHANGE] cart ←', cart);
  return cart;
}

/* ========== UI memory (localStorage) — by VARIANT ID ========== */

// Build stable storage key. Prefer variant id; fall back to key if missing (shouldn't be).
function storageKeyPrimary(container){
  return 'planSel:v:' + (container?.dataset?.variantId || '');
}
function storageKeyLegacy(container){
  return 'planSel:' + (container?.dataset?.key || ''); // old scheme (by line key)
}
function readRemembered(container){
  try {
    const primary = localStorage.getItem(storageKeyPrimary(container));
    if (primary) return primary;

    // migrate legacy (by key) → primary (by variantId), then delete legacy
    const legacy = localStorage.getItem(storageKeyLegacy(container));
    if (legacy) {
      localStorage.setItem(storageKeyPrimary(container), legacy);
      localStorage.removeItem(storageKeyLegacy(container));
      return legacy;
    }
  } catch(e){}
  return null;
}
function writeRemembered(container, planId){
  try {
    const k = storageKeyPrimary(container);
    if (planId) localStorage.setItem(k, String(planId));
    else localStorage.removeItem(k);
  } catch(e){}
}

function rememberPlanSelection(container, planId){
  writeRemembered(container, planId || '');
}

/**
 * Hydrate from memory if present (by VARIANT), otherwise fall back to
 * server-rendered data-selected-plan (allocation).
 */
function hydrateSelectionFromMemory(scope = document) {
  scope.querySelectorAll('.subscribe-container').forEach((c) => {
    const select   = c.querySelector('select[name="selling_plan"]');
    const checkbox = c.querySelector('input[name="subscribe_checkbox"]');
    if (!select || !checkbox) return;

    const fromData  = c.getAttribute('data-selected-plan'); // server truth
    const fromStore = readRemembered(c);                     // local memory
    let planId      = null;

    if (fromData) {
      // Always trust the server allocation
      planId = String(fromData);

      // If memory disagrees, overwrite it
      if (fromStore !== planId) {
        writeRemembered(c, planId);
      }
    } else if (fromStore) {
      // No allocation (one-time purchase) but we had an old remembered value
      planId = String(fromStore);
    }

    // If still nothing, nothing to hydrate
    if (!planId) {
      return;
    }

    const opt = select.querySelector(`option[value="${planId}"]`);
    if (opt) {
      checkbox.checked = true;
      select.style.display = 'block';
      select.value = planId;

      select.querySelectorAll('option').forEach((o) =>
        o.removeAttribute('selected')
      );
      opt.setAttribute('selected', 'selected');
    }
  });
}


// Install a MutationObserver to catch drawer section swaps
function installDrawerObserver(){
  const root = document.getElementById('CartDrawer-CartItems') || document;
  if (!root) return;
  const mo = new MutationObserver(() => {
    // When the cart DOM is replaced, re-apply the remembered selection
    hydrateSelectionFromMemory(document);
  });
  mo.observe(root, { childList: true, subtree: true });
  window.__cartDrawerHydrator = mo;
}

/* =============== unified updater =============== */

async function updateSellingPlan(container, rawPlanId){
  const planId = validPlanForContainer(container, rawPlanId);
  try {
    const afterChange = await changeLineSellingPlan(container, planId);

    // Let theme JS know cart changed (so sections can re-render)
    if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'subscribe-ui', cartData: afterChange });
    }

    // Force-refresh drawer HTML so prices update immediately
    await refreshCartDrawer();

  } catch (err){
    console.error('updateSellingPlan error →', err);
    // Even on error, try to refresh the drawer to keep UI consistent
    await refreshCartDrawer();
  }
}

/* ================== delegated UI handlers ================== */

document.addEventListener('change', (e) => {
  // Toggle subscribe checkbox
  if (e.target.matches('.subscribe-container input[name="subscribe_checkbox"]')){
    const c   = e.target.closest('.subscribe-container');
    const sel = c.querySelector('select[name="selling_plan"]');
    if (!sel) return;

    const planId = e.target.checked ? sel.value : '';
    sel.style.display = e.target.checked ? 'block' : 'none';

    // remember for refresh
    rememberPlanSelection(c, planId);

    updateSellingPlan(c, planId);
  }

  // Change frequency
  if (e.target.matches('.subscribe-container select[name="selling_plan"]')){
    const c  = e.target.closest('.subscribe-container');
    const cb = c.querySelector('input[name="subscribe_checkbox"]');
    if (!cb) return;

    const planId = cb.checked ? e.target.value : '';

    // remember for refresh
    rememberPlanSelection(c, planId);

    updateSellingPlan(c, planId);
  }
});

/* ================= hydrate on load & on redraw ================= */

document.addEventListener('DOMContentLoaded', () => {
  hydrateSelectionFromMemory(document);
  installDrawerObserver();
});

// Sense will publish after it replaces the drawer HTML
if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    // Let the DOM swap finish, then hydrate
    setTimeout(() => hydrateSelectionFromMemory(document), 0);
  });
}
